// One-shot, sentinel-guarded, idempotent contacts migration.
//
// D-01: Non-destructive migration — maps legacy helpers[] to relatedPeople[] (role: observer).
//       Sentinel = Array.isArray(contact.relatedPeople). Already-migrated contacts are skipped.
// D-04: helpers[] is retained (never deleted) during the beta so V1 Clients keeps working.
// D-05: Destructive helpers[] cleanup is authored and gated by isV1ClientsRetired = false.
//       It must NOT run until V1 is retired.
// D-15: preferredLanguage is backfilled to English ('en') for all contacts that lack it.

import React from 'react';
import { isEntityContact, sanitizeRelatedPeople } from '../utils/contactKind';
import { upsertContacts as _defaultUpsertContacts } from '../services/workspaceService';

// The canonical default for the preferredLanguage field (D-15).
// English is pinned and NOT in CURATED_LOCALES; 'en' is the project's English code.
export const DEFAULT_PREFERRED_LANGUAGE = 'en';

// Maximum batch size for Firestore write batches.
const BATCH_SIZE = 400;

/**
 * Build the additive migration payload for a single contact.
 *
 * For individual contacts: maps helpers[] entries to relatedPeople[] with role 'observer'.
 * For entity contacts: relatedPeople is always [] (entities use representatives[]).
 * Always sets preferredLanguage if not already present.
 * NEVER includes a helpers key in the payload (non-destructive, D-04).
 *
 * Exported for unit testing.
 */
export function buildContactMigrationPayload(contact) {
    // Build the relatedPeople array.
    let relatedPeople;
    if (isEntityContact(contact)) {
        // Entities use representatives[], never relatedPeople helpers content.
        relatedPeople = [];
    } else {
        // Map legacy helpers to observer-role relatedPeople entries.
        const rawHelpers = Array.isArray(contact.helpers) ? contact.helpers : [];
        const withRole = rawHelpers.map(h => ({ ...h, role: 'observer' }));
        relatedPeople = sanitizeRelatedPeople(withRole);
    }

    const preferredLanguage = contact.preferredLanguage || DEFAULT_PREFERRED_LANGUAGE;

    // Return ONLY additive fields — never helpers (non-destructive).
    return {
        id: contact.id,
        relatedPeople,
        preferredLanguage,
    };
}

/**
 * Given an array of contacts, return the subset that needs a write,
 * with their computed migration payloads.
 *
 * A contact needs a write if:
 *   - relatedPeople is not yet set (missing sentinel), OR
 *   - preferredLanguage is missing.
 *
 * Exported for unit testing.
 */
export function buildContactWriteList(contacts) {
    const result = [];
    for (const contact of contacts) {
        const needsRelatedPeople = !Array.isArray(contact.relatedPeople);
        const needsLanguage = !contact.preferredLanguage;
        if (needsRelatedPeople || needsLanguage) {
            result.push(buildContactMigrationPayload(contact));
        }
    }
    return result;
}

/**
 * Chunk an array into sub-arrays of at most `size` items.
 */
function chunkArray(arr, size) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}

/**
 * Gated destructive helpers[] cleanup (D-05).
 *
 * When isV1ClientsRetired is false (the default), this is a no-op — it must not
 * execute while V1 Clients is the default page.
 *
 * When isV1ClientsRetired is true, it would write payloads that clear helpers[]
 * (e.g. via FieldValue.delete() equivalent). That path is authored but not yet
 * reachable; it will be completed when V1 is retired.
 *
 * The `_upsert` parameter is an injection point for testing (defaults to the real service).
 *
 * Exported for unit testing.
 */
export async function cleanupLegacyHelpers(
    workspaceId,
    contacts,
    { isV1ClientsRetired = false, _upsert = _defaultUpsertContacts } = {}
) {
    // D-05 gate: early-return while V1 Clients is the default page.
    if (!isV1ClientsRetired) return;

    // This path will be implemented when V1 is retired and the gate is lifted.
    // At that point, build payloads with helpers: deleteField()-equivalent and
    // write them. Until then, this block is intentionally unreachable.
    if (!workspaceId || !Array.isArray(contacts)) return;
    try {
        const toClean = contacts.filter(c => Array.isArray(c.helpers) && c.helpers.length > 0);
        const payloads = toClean.map(c => ({ id: c.id, helpers: [] }));
        const chunks = chunkArray(payloads, BATCH_SIZE);
        for (const chunk of chunks) {
            await _upsert(workspaceId, chunk);
        }
    } catch (err) {
        console.warn('Contacts helpers cleanup failed:', err);
    }
}

/**
 * React hook returning { runContactsMigration }.
 *
 * runContactsMigration(workspaceId, contacts):
 *   - Guards workspaceId and contacts array.
 *   - Computes the write list (contacts failing the sentinel or missing preferredLanguage).
 *   - Writes in batches of ≤ 400 using upsertContacts (merge: true — idempotent).
 *   - Errors are caught and swallowed with console.warn (never crashes the app).
 */
export function useContactsMigration() {
    const runContactsMigration = React.useCallback(async (workspaceId, contacts) => {
        if (!workspaceId || !Array.isArray(contacts)) return;

        try {
            const writeList = buildContactWriteList(contacts);
            if (writeList.length === 0) return;

            const chunks = chunkArray(writeList, BATCH_SIZE);
            for (const chunk of chunks) {
                await _defaultUpsertContacts(workspaceId, chunk);
            }
        } catch (err) {
            console.warn('Contacts migration failed:', err);
        }
    }, []);

    return { runContactsMigration };
}
